/**
 * 🆕 [Phase 51 BIS — Addendum] Helpers purs frontend pour le calendrier.
 *
 * IMPORTANT : algorithme identique à `backend/lib/calendar_phase51.py`.
 * Si tu modifies la logique ici, modifie aussi le Python (testé par pytest).
 *
 * Activable via le feature flag `REACT_APP_ENABLE_PHASE51_ADDENDUM` (défaut: true).
 */

export const ENABLE_PHASE51_ADDENDUM = process.env.REACT_APP_ENABLE_PHASE51_ADDENDUM !== 'false';

/**
 * @param {Array} bts — liste de BT {id, dates_chantier, chef_equipe_id,
 *                       ordre_journalier, ordres_par_jour, numero_bt}
 * @returns {Map<string, Array<{bt_id, position}>>}  clé = `team_id__YYYY-MM-DD`,
 *          présent uniquement si l'équipe a ≥ 2 BT distincts ce jour-là.
 */
export function computeBadgesParCellule(bts) {
  const parCellule = new Map();
  for (const bt of bts) {
    const teamId = bt.chef_equipe_id || bt.team_id || '';
    if (!teamId) continue;
    const dates = bt.dates_chantier || [];
    for (const date of dates) {
      const key = `${teamId}__${date}`;
      if (!parCellule.has(key)) parCellule.set(key, []);
      parCellule.get(key).push(bt);
    }
  }
  const result = new Map();
  for (const [key, btsInCell] of parCellule.entries()) {
    const seen = new Map();
    for (const b of btsInCell) {
      if (!seen.has(b.id)) seen.set(b.id, b);
    }
    const unique = Array.from(seen.values());
    if (unique.length < 2) continue;
    const date = key.split('__')[1];
    unique.sort((a, b) => {
      const opjA = (a.ordres_par_jour && a.ordres_par_jour[date] != null)
        ? a.ordres_par_jour[date]
        : (a.ordre_journalier != null ? a.ordre_journalier : 99);
      const opjB = (b.ordres_par_jour && b.ordres_par_jour[date] != null)
        ? b.ordres_par_jour[date]
        : (b.ordre_journalier != null ? b.ordre_journalier : 99);
      if (opjA !== opjB) return opjA - opjB;
      const numA = a.numero_bt || '';
      const numB = b.numero_bt || '';
      if (numA !== numB) return numA < numB ? -1 : 1;
      return (a.id || '') < (b.id || '') ? -1 : 1;
    });
    result.set(key, unique.map((b, idx) => ({ bt_id: b.id, position: idx })));
  }
  return result;
}

/**
 * @param {Object} bt  — {dates_chantier, chef_equipe_id, chefs_par_jour}
 * @param {Object} presentsParJour — {YYYY-MM-DD: [employee_ids présents]}
 * @returns {Object}  {YYYY-MM-DD: bool}  — true si chef présent ce jour-là.
 */
export function computeChefPresentParCellule(bt, presentsParJour) {
  const out = {};
  const chefDefault = bt.chef_equipe_id;
  const chefsParJour = bt.chefs_par_jour || {};
  const dates = bt.dates_chantier || [];
  for (const date of dates) {
    const chefDuJour = chefsParJour[date] || chefDefault;
    if (!chefDuJour) {
      out[date] = true;  // pas de chef assigné = comportement existant inchangé
      continue;
    }
    const presents = presentsParJour[date] || [];
    out[date] = presents.includes(chefDuJour);
  }
  return out;
}
